import * as THREE from 'three'

/**
 * Creates a SOLID clipped material for shapes inside the cage.
 * Matching the reference index.html - NO transparency, solid colors.
 * Cross-sections are computed geometrically by CrossSectionComputer.
 */
export function createClippedShapeMaterial(baseColor, clippingPlanes) {
  // Compute a darker emissive color for subtle depth
  const color = new THREE.Color(baseColor)
  const emissiveColor = color.clone().multiplyScalar(0.25)
  
  return new THREE.MeshStandardMaterial({
    color: color,
    emissive: emissiveColor,
    emissiveIntensity: 0.2,
    metalness: 0.4,
    roughness: 0.4,
    side: THREE.DoubleSide,
    clippingPlanes: clippingPlanes,
    clipShadows: true,
    transparent: false,
    depthWrite: true,
    depthTest: true
  })
}

/**
 * Creates a glowing holographic material for team shapes
 */
export function createHolographicMaterial(baseColor, clippingPlanes) {
  const planeVectors = []
  for (let i = 0; i < clippingPlanes.length; i++) {
    const plane = clippingPlanes[i]
    planeVectors.push(new THREE.Vector4(
      plane.normal.x,
      plane.normal.y,
      plane.normal.z,
      plane.constant
    ))
  }
  
  return new THREE.ShaderMaterial({
    uniforms: {
      baseColor: { value: new THREE.Color(baseColor) },
      opacity: { value: 0.9 },
      clippingPlanes: { value: planeVectors },
      time: { value: 0 },
      glowIntensity: { value: 0.5 }
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      varying vec3 vViewPosition;
      
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        vNormal = normalize(normalMatrix * normal);
        
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = -mvPosition.xyz;
        
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 baseColor;
      uniform float opacity;
      uniform vec4 clippingPlanes[6];
      uniform float time;
      uniform float glowIntensity;
      
      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      varying vec3 vViewPosition;
      
      void main() {
        // Clipping logic
        for (int i = 0; i < 6; i++) {
          vec3 planeNormal = clippingPlanes[i].xyz;
          float planeConstant = clippingPlanes[i].w;
          float signedDist = dot(vWorldPosition, planeNormal) + planeConstant;
          
          if (signedDist > 0.0) {
            discard;
          }
        }
        
        // View direction and normal
        vec3 viewDir = normalize(vViewPosition);
        vec3 normal = normalize(vNormal);
        
        // Fresnel rim effect
        float fresnel = pow(1.0 - abs(dot(viewDir, normal)), 2.5);
        
        // Basic lighting
        vec3 lightDir = normalize(vec3(0.5, 1.0, 0.5));
        float diffuse = max(dot(normal, lightDir), 0.0) * 0.5 + 0.5;
        
        // Combine color with fresnel glow
        vec3 finalColor = baseColor * diffuse;
        finalColor += baseColor * fresnel * glowIntensity;
        finalColor += baseColor * 0.2; // Ambient emissive
        
        gl_FragColor = vec4(finalColor, opacity);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: false,
    toneMapped: false
  })
}
